import React, { Suspense } from 'react';
import PropTypes from 'prop-types';

export const lazy = resolve => {
  const LazyLoaded = React.lazy(resolve);
  const LazyComponent = (props = {}) => {
    const { visible } = props;
    const shouldRender = typeof visible === 'undefined' ? true : visible;
    return shouldRender ? (
      <Suspense fallback={null}>
        <LazyLoaded {...props} />
      </Suspense>
    ) : null;
  };
  LazyComponent.propTypes = { visible: PropTypes.bool };

  return React.memo(LazyComponent);
};

export const viewportIntersection = (resolve, componentConfig) => {
  const LazyLoaded = React.lazy(resolve);

  return class LazyComponent extends React.PureComponent {
    static propTypes = {
      visible: PropTypes.bool,
    };

    intersecting = false;

    handle = React.createRef();

    componentDidMount() {
      if ('IntersectionObserver' in window) {
        this.setupIntersectionObserver();
      } else {
        this.intersecting = true;
        this.forceUpdate();
      }
    }

    componentWillUnmount() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    setupIntersectionObserver() {
      const { intersectionObserver: customConfig } = componentConfig;
      const defaultConfig = { threshold: 0.0 };

      const callback = entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && this.intersecting === false) {
            this.intersecting = true;
            this.forceUpdate();
          }
        });
      };

      const initObserver = config => {
        this.observer = new IntersectionObserver(callback, config);
        this.observer.observe(this.handle.current);
      };

      try {
        initObserver(customConfig || defaultConfig);
      } catch (e) {
        console.error(
          `Incorrect IntersectionObserver config passed from the presenter.
           The default configuration has been applied.`
        );
        initObserver(defaultConfig);
      }
    }

    render() {
      const { visible } = this.props;

      const shouldRender = typeof visible === 'undefined' ? true : visible;
      return (
        <React.Fragment>
          {this.intersecting === false && <span ref={this.handle} />}
          {this.intersecting && shouldRender && (
            <Suspense fallback={null}>
              <LazyLoaded {...this.props} />
            </Suspense>
          )}
        </React.Fragment>
      );
    }
  };
};
